// Mandatory upload (§5.1, §6.1/6.2). Display name + dropzone; Save disabled
// until a zip parses. On save: create_profile -> dashboard, or if arriving via
// invite, create_blend -> blend.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Page } from "../components/Layout.tsx";
import { Dropzone } from "../components/Dropzone.tsx";
import { useAuth } from "../auth/AuthProvider.tsx";
import { consumePendingInvite } from "../auth/AuthProvider.tsx";
import {
  createProfile,
  createBlend,
  getMyProfile,
  getInviteTeaser,
} from "../lib/api.ts";
import { defaultDisplayName } from "../lib/anonMode.ts";
import { track, clientHints } from "../lib/analytics.ts";
import type { DistilledProfile } from "../lib/types.ts";

export default function Onboarding() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState("");
  const [profile, setProfile] = useState<DistilledProfile | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invite, setInvite] = useState<{ slug: string; name: string } | null>(
    null,
  );

  useEffect(() => {
    if (auth.loading) return;
    if (!auth.user) {
      track("onboarding_bounce_no_user", clientHints());
      navigate("/", { replace: true });
      return;
    }
    track("onboarding_reached", { guest: auth.isAnonymous });
    // Pre-fill display name from OAuth metadata; guests start blank.
    setDisplayName((prev) => prev || defaultDisplayName(auth.user));

    // If a profile already exists, onboarding is unreachable.
    getMyProfile()
      .then((p) => {
        if (p) navigate("/me", { replace: true });
      })
      .catch(() => {});

    // Invite context banner (§6.2): peek at pendingInvite without consuming.
    try {
      const pending = localStorage.getItem("pendingInvite");
      if (pending) {
        getInviteTeaser(pending)
          .then((t) => t && setInvite({ slug: pending, name: t.display_name }))
          .catch(() => {});
      }
    } catch {
      /* ignore */
    }
  }, [auth.loading, auth.user, navigate]);

  async function save() {
    if (!profile || !displayName.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await createProfile(displayName.trim(), profile);
      const pending = consumePendingInvite();
      if (pending) {
        const res = await createBlend(pending);
        if (res.blend_slug) {
          navigate(`/b/${res.blend_slug}`, { replace: true });
          return;
        }
      }
      navigate("/me", { replace: true });
    } catch (err) {
      setError((err as Error).message);
      setSaving(false);
    }
  }

  return (
    <Page testid="screen-onboarding">
      <h1 className="text-2xl font-semibold text-ink-900">
        One step: your library
      </h1>
      <p className="mt-1 text-ink-400">
        twoshot needs your film data before it can do anything. Nothing else.
      </p>

      {invite && (
        <div
          data-testid="invite-banner"
          className="mt-4 rounded-lg border border-green-500/40 bg-card p-3 text-sm"
        >
          Blending with <strong className="text-ink-900">{invite.name}</strong>{" "}
          after this step.
        </div>
      )}

      {auth.isAnonymous && (
        <div
          data-testid="guest-banner"
          className="mt-4 rounded-lg border border-border-strong bg-card p-3 text-sm text-ink-500"
        >
          You're browsing as a <strong className="text-ink-900">guest</strong> —
          pick any name. Your public page stays hidden unless you turn it on,
          and you can claim this profile with Discord or Spotify later.
        </div>
      )}

      {/* Step 1: get the export straight from Letterboxd. */}
      <div
        className="mt-6 rounded-xl border border-border-strong bg-card p-4"
        data-testid="export-help"
      >
        <p className="text-sm font-medium text-ink-900">
          Don't have your export yet?
        </p>
        <p className="mt-1 text-sm text-ink-400">
          Tap below and Letterboxd downloads your{" "}
          <code className="rounded bg-paper-100 px-1 text-ink-900">.zip</code>{" "}
          straight away — then drop that here.
        </p>
        <a
          href="https://letterboxd.com/data/export/"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="action-get-export"
          className="btn-primary mt-3"
        >
          Download my export from Letterboxd
          <ExternalIcon />
        </a>
        <p className="mt-2 text-xs text-ink-400">
          Opens in a new tab. If you're not signed in to Letterboxd, it'll ask
          you to log in first, then download.
        </p>
      </div>

      <div className="mt-6 space-y-4">
        <label className="block">
          <span className="text-sm text-ink-400">Display name</span>
          <input
            data-testid="action-display-name"
            className="mt-1 w-full rounded-lg border border-border-strong bg-card px-3 py-2.5 text-ink-900 focus:border-gold-500 focus:outline-none"
            value={displayName}
            maxLength={40}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Your name"
          />
        </label>

        <Dropzone onParsed={setProfile} />

        {error && <p className="text-sm text-red-600">{error}</p>}

        <button
          type="button"
          data-testid="action-save-profile"
          className="btn-primary w-full"
          disabled={!profile || !displayName.trim() || saving}
          onClick={save}
        >
          {saving
            ? "Saving…"
            : invite
              ? `Save & blend with ${invite.name}`
              : "Save & see my dashboard"}
        </button>
      </div>
    </Page>
  );
}

const ExternalIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden
  >
    <path
      d="M14 4h6v6M20 4l-8 8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M18 14v4a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2h4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
